import { useEffect, useState } from "react";
import { collection, query, where, onSnapshot, type DocumentData } from "firebase/firestore";
import { db, call } from "../firebase";
import { useSession } from "../session";
import { SITES, DOC_CATEGORIES, isGlobal, canSeeTrash, type Member } from "../domain";
import { friendly, toast } from "../errors";
import { ask } from "../dialog";

type Row = DocumentData & { id: string };
const DAY = 86400000;

function trashQuery(m: Member) {
  const c = [where("status", "==", "trashed")];
  if (!isGlobal(m)) c.push(where("siteId", "in", m.sites.length ? m.sites : ["-"]));
  return query(collection(db, "documents"), ...c);
}

/** DOC-10: tài liệu đã xóa, khôi phục được trong 30 ngày; sau đó Hub tự xóa vĩnh viễn. */
export default function Trash() {
  const { member } = useSession();
  const [rows, setRows] = useState<Row[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const scopeKey = member ? `${member.role}|${member.sites.join(",")}` : "";

  useEffect(() => {
    if (!member) return;
    return onSnapshot(trashQuery(member), (s) => {
      const list: Row[] = s.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (b.trash?.at?.toMillis?.() ?? 0) - (a.trash?.at?.toMillis?.() ?? 0));
      setRows(list);
    }, (e) => setErr(friendly(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  if (!member) return null;
  if (!canSeeTrash(member)) return <><h1>Thùng rác</h1><div className="card">Bạn không có quyền xem thùng rác.</div></>;

  const restore = async (r: Row) => {
    setBusy(r.id);
    try { await call("restoreDocument")({ docId: r.id }); toast(`Đã khôi phục ${r.code}.`, "ok"); } catch (e) { toast(friendly(e)); }
    setBusy(null);
  };
  const purge = async (r: Row) => {
    if (!await ask({ title: `Xóa vĩnh viễn ${r.code}?`, danger: true, confirm: "Xóa vĩnh viễn",
      message: <><b>{r.title}</b><br />File gốc và mọi phiên bản sẽ bị xóa hẳn khỏi kho. <b>Không khôi phục được nữa.</b></> })) return;
    setBusy(r.id);
    try { await call("purgeDocumentNow")({ docId: r.id }); toast(`Đã xóa vĩnh viễn ${r.code}.`, "ok"); } catch (e) { toast(friendly(e)); }
    setBusy(null);
  };

  return (
    <>
      <h1>Thùng rác</h1>
      <p className="sub">Tài liệu đã xóa được giữ 30 ngày để khôi phục, sau đó Hub tự xóa vĩnh viễn. Mã tài liệu đã xóa không cấp lại cho tài liệu khác.</p>
      {err && <div className="err" style={{ marginBottom: 12 }}>{err}</div>}
      <div className="card tableWrap" style={{ padding: 0 }}>
        <table className="table stack">
          <thead><tr><th>Mã</th><th>Tài liệu</th><th>Cơ sở</th><th>Xóa bởi</th><th>Còn lại</th><th></th></tr></thead>
          <tbody>
            {rows.map((r) => {
              const at = r.trash?.at?.toMillis?.() ?? Date.now();
              const left = Math.max(0, 30 - Math.floor((Date.now() - at) / DAY));
              return (
                <tr key={r.id}>
                  <td className="code cardHead">{r.code ?? "—"}</td>
                  <td>{r.title}<div className="muted">{DOC_CATEGORIES[r.category]}{r.trash?.reason ? ` · Lý do: ${r.trash.reason}` : ""}</div></td>
                  <td data-label="Cơ sở">{SITES[r.siteId]}</td>
                  <td data-label="Xóa bởi">{r.trash?.by}<div className="muted">{new Date(at).toLocaleString("vi-VN")}</div></td>
                  <td data-label="Còn lại"><span className={`pill ${left <= 3 ? "changes_requested" : ""}`}>{left} ngày</span></td>
                  <td className="actions"><div className="row-actions">
                    {(member.role !== "site_mgr" || member.sites.includes(r.siteId)) && <button className="btn primary" disabled={busy === r.id} onClick={() => restore(r)}>Khôi phục</button>}
                    {member.role === "admin" && <button className="btn" disabled={busy === r.id} onClick={() => purge(r)}>Xóa vĩnh viễn</button>}
                  </div></td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan={6} className="muted">Thùng rác trống.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}
